<template>
  <CreateUpdateTemplate
    :routePreventDialog="routePreventDialog"
    :hasChanges="hasChanges"
    :customClass="'create-contract create-form'"
    v-if="getPermission('contract:create') || getPermission('contract:update')"
  >
    <template v-slot:header-title>
      <div class="d-flex justify-space-between">
        <h1 class="form-title text-truncate d-flex align-baseline">
          Contract for

          <div
            v-if="vCustomerId"
            class="ml-3 form-title-link cursor-pointer text-h5"
            v-on:click="openDialog('customer')"
          >
            {{ vCustomer.display_name }}
          </div>
          <template v-else>
            <div
              class="ml-3 form-title-link cursor-pointer text-h5"
              v-on:click="openDialog('customer')"
            >
              Select a Customer
              <v-icon large color="cyan">mdi-account-check-outline</v-icon>
            </div>
            <div class="mx-3">OR</div>
            <div
              class="form-title-link cursor-pointer cyan--text text--darken-4 text-h5"
              v-on:click="createCustomerDialog()"
            >
              Create a new Customer
              <v-icon large color="cyan darken-4"
                >mdi-plus-circle-outline</v-icon
              >
            </div>
          </template>
        </h1>
        <v-chip
          label
          color="chip-custom-blue"
          outlined
          class="text-white p-3 mr-4"
          style="font-size: 21px !important; font-weight: 700"
        >
          <template>
            {{ contractCreate.barcode }}
          </template>
        </v-chip>
      </div>
    </template>
    <template v-slot:header-action>
      <v-chip
        label
        color="chip-custom-blue"
        outlined
        class="text-white p-3"
        style="font-size: 21px !important; font-weight: 700"
        >{{ contractCreate.barcode }}</v-chip
      >
      <v-btn
        v-on:click="goBack()"
        :disabled="formLoading || pageLoading"
        class="mx-2 custom-grey-border custom-bold-button"
        depressed
      >
        Cancel
      </v-btn>
      <v-btn
        depressed
        :disabled="!formValid || formLoading || pageLoading"
        :loading="formLoading"
        class="mx-2 custom-bold-button white--text"
        color="cyan"
        v-on:click="updateOrCreate()"
      >
        Save as Draft
      </v-btn>
      <v-btn
        depressed
        :disabled="!formValid || formLoading || pageLoading"
        :loading="formLoading"
        class="mx-2 custom-bold-button white--text"
        color="cyan"
        v-on:click="updateOrCreate(_, 'mark_as_approved')"
      >
        Save and Approve
      </v-btn>
    </template>
    <template v-slot:body>
      <perfect-scrollbar
        :options="{ suppressScrollX: true }"
        class="scroll custom-box-top-inner-shadow product-create-height"
        style="height: calc(100vh - 150px); position: relative"
      >
        <div class="bg-white px-2">
          <v-form
            ref="contractForm"
            v-model.trim="formValid"
            lazy-validation
            v-on:submit.stop.prevent="updateOrCreate()"
          >
            <v-row>
              <v-col md="7" class="pt-0">
                <label class="font-size-16 font-weight-500 required"
                  >Contract Title</label
                >
                <TextField
                  id="title"
                  dense
                  filled
                  placeholder="Title"
                  solo
                  flat
                  counter="100"
                  v-model="contractCreate.job_title"
                  color="cyan"
                  :maxlength="100"
                />
                <v-text-field
                  v-if="false"
                  v-model.trim="contractCreate.job_title"
                  dense
                  filled
                  placeholder="Title"
                  solo
                  flat
                  :disabled="pageLoading"
                  :loading="pageLoading"
                  color="cyan"
                  :rules="[
                    validateRules.required(
                      contractCreate.job_title,
                      'Contract Title'
                    ),
                    validateRules.minLength(
                      contractCreate.job_title,
                      'Contract Title',
                      1
                    ),
                    validateRules.maxLength(
                      contractCreate.job_title,
                      'Contract Title',
                      100
                    ),
                  ]"
                  :maxlength="100"
                ></v-text-field>
                <TextAreaField
                  auto-grow
                  dense
                  filled
                  color="cyan"
                  v-model="contractCreate.description"
                  placeholder="Remark"
                  solo
                  flat
                  row-height="25"
                  counter="200"
                />
                <v-row class="mt-1">
                  <!-- Billing Location Card -->
                  <v-col md="6" class="d-flex flex-column">
                    <v-card
                      flat
                      outlined
                      class="rounded-lg bg-slate-50 pa-3 h-100 border-slate"
                    >
                      <div
                        class="d-flex align-center justify-space-between pb-2 mb-2 custom-border-bottom"
                      >
                        <div class="d-flex align-center">
                          <v-icon color="cyan darken-2" small class="mr-2"
                            >mdi-map-marker-radius-outline</v-icon
                          >
                          <span
                            class="font-weight-700 font-size-14 color-custom-blue"
                            >Billing Location</span
                          >
                        </div>
                        <v-btn
                          icon
                          x-small
                          color="cyan"
                          class="bg-white custom-grey-border"
                          v-on:click="openDialog('billing')"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>

                      <template v-if="vBilling?.id">
                        <div class="mb-2">
                          <div
                            class="font-weight-600 font-size-13 text-slate-800 d-flex align-center"
                            v-if="vBilling.property_name"
                          >
                            <v-icon small color="slate-600" class="mr-1"
                              >mdi-home-map-marker</v-icon
                            >
                            {{ vBilling.property_name }}
                          </div>
                          <div
                            v-else
                            class="text-muted font-italic font-size-12"
                          >
                            <v-icon small color="slate-400" class="mr-1"
                              >mdi-home-map-marker</v-icon
                            >
                            no Site Name
                          </div>

                          <div
                            class="d-flex align-start font-size-12 text-slate-700 mt-1"
                          >
                            <v-icon small color="slate-500" class="mr-1 mt-1"
                              >mdi-map-marker-outline</v-icon
                            >
                            <div>
                              <template v-if="vBilling.unit_no"
                                >{{ vBilling.unit_no }},
                              </template>
                              <template v-if="vBilling.street_1"
                                >{{ vBilling.street_1 }},
                              </template>
                              {{ vBilling.street_2 }}
                              <div v-if="vBilling.country">
                                {{ vBilling.country }}
                                <template
                                  v-if="
                                    vBilling.zip_code &&
                                    vBilling.zip_code != '000000'
                                  "
                                >
                                  {{ vBilling.zip_code }}
                                </template>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="vCustomer?.id">
                        <p class="mb-2 font-size-12 red--text font-weight-500">
                          Please select billing location
                        </p>
                      </template>
                      <template v-else>
                        <p class="mb-2 font-size-12 red--text font-weight-500">
                          Please select customer
                        </p>
                      </template>

                      <!-- Contact Details Sub-section -->
                      <div
                        class="d-flex align-center justify-space-between pb-2 mb-2 custom-border-bottom pt-2 border-top-slate"
                      >
                        <div class="d-flex align-center">
                          <v-icon color="cyan darken-2" small class="mr-2"
                            >mdi-account-outline</v-icon
                          >
                          <span
                            class="font-weight-700 font-size-14 color-custom-blue"
                            >Contact Details</span
                          >
                        </div>
                        <v-btn
                          icon
                          x-small
                          color="cyan"
                          class="bg-white custom-grey-border"
                          v-on:click="openDialog('billing-contact')"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>
                      <template v-if="vBillingContact?.id">
                        <div class="font-size-12 text-slate-800 mt-1">
                          <div class="font-weight-600 color-custom-blue">
                            <v-icon small color="slate-600" class="mr-1"
                              >mdi-account-outline</v-icon
                            >
                            {{ vBillingContact.display_name }}
                          </div>
                          <div
                            v-if="vBillingContact.primary_phone"
                            class="text-slate-600"
                          >
                            <v-icon small color="slate-500" class="mr-1"
                              >mdi-phone-outline</v-icon
                            >
                            {{ vBillingContact.primary_phone }}
                          </div>
                          <div
                            v-if="vBillingContact.primary_email"
                            class="text-slate-600"
                          >
                            <v-icon small color="slate-500" class="mr-1"
                              >mdi-email-outline</v-icon
                            >
                            {{ vBillingContact.primary_email }}
                          </div>
                        </div>
                      </template>
                      <template v-else-if="vCustomer?.id">
                        <p class="mb-0 font-size-12 red--text font-weight-500">
                          Please select contact person
                        </p>
                      </template>
                      <template v-else>
                        <p class="mb-0 font-size-12 red--text font-weight-500">
                          Please select customer
                        </p>
                      </template>
                    </v-card>
                  </v-col>

                  <!-- Site Location Card -->
                  <v-col md="6" class="d-flex flex-column">
                    <v-card
                      flat
                      outlined
                      class="rounded-lg bg-slate-50 pa-3 h-100 border-slate"
                    >
                      <div
                        class="d-flex align-center justify-space-between pb-2 mb-2 custom-border-bottom"
                      >
                        <div class="d-flex align-center">
                          <v-icon color="cyan darken-2" small class="mr-2"
                            >mdi-map-marker-radius-outline</v-icon
                          >
                          <span
                            class="font-weight-700 font-size-14 color-custom-blue"
                            >Site Location</span
                          >
                        </div>
                        <v-btn
                          icon
                          x-small
                          color="cyan"
                          class="bg-white custom-grey-border"
                          v-on:click="openDialog('property')"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>

                      <template v-if="vProperty?.id">
                        <div class="mb-2">
                          <div
                            class="font-weight-600 font-size-13 text-slate-800 d-flex align-center"
                            v-if="vProperty?.property_name"
                          >
                            <v-icon small color="slate-600" class="mr-1"
                              >mdi-home-map-marker</v-icon
                            >
                            {{ vProperty?.property_name }}
                          </div>
                          <div
                            v-else
                            class="text-muted font-italic font-size-12"
                          >
                            <v-icon small color="slate-400" class="mr-1"
                              >mdi-home-map-marker</v-icon
                            >
                            no Site Name
                          </div>

                          <div
                            class="d-flex align-start font-size-12 text-slate-700 mt-1"
                          >
                            <v-icon small color="slate-500" class="mr-1 mt-1"
                              >mdi-map-marker-outline</v-icon
                            >
                            <div>
                              <template v-if="vProperty?.unit_no"
                                >{{ vProperty?.unit_no }},
                              </template>
                              <template v-if="vProperty?.street_1"
                                >{{ vProperty?.street_1 }},
                              </template>
                              {{ vProperty?.street_2 }}
                              <div v-if="vProperty?.country">
                                {{ vProperty?.country }}
                                <template
                                  v-if="
                                    vProperty?.zip_code &&
                                    vProperty?.zip_code != '000000'
                                  "
                                >
                                  {{ vProperty?.zip_code }}
                                </template>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="vCustomer?.id">
                        <p class="mb-2 font-size-12 red--text font-weight-500">
                          Please select site location
                        </p>
                      </template>
                      <template v-else>
                        <p class="mb-2 font-size-12 red--text font-weight-500">
                          Please select customer
                        </p>
                      </template>

                      <!-- Contact Details Sub-section -->
                      <div
                        class="d-flex align-center justify-space-between pb-2 mb-2 custom-border-bottom pt-2 border-top-slate"
                      >
                        <div class="d-flex align-center">
                          <v-icon color="cyan darken-2" small class="mr-2"
                            >mdi-account-outline</v-icon
                          >
                          <span
                            class="font-weight-700 font-size-14 color-custom-blue"
                            >Contact Details</span
                          >
                        </div>
                        <v-btn
                          icon
                          x-small
                          color="cyan"
                          class="bg-white custom-grey-border"
                          v-on:click="openDialog('property-contact')"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>
                      <template v-if="vPropertyContact?.id">
                        <div class="font-size-12 text-slate-800 mt-1">
                          <div class="font-weight-600 color-custom-blue">
                            <v-icon small color="slate-600" class="mr-1"
                              >mdi-account-outline</v-icon
                            >
                            {{ vPropertyContact.display_name }}
                          </div>
                          <div
                            v-if="vPropertyContact.primary_phone"
                            class="text-slate-600"
                          >
                            <v-icon small color="slate-500" class="mr-1"
                              >mdi-phone-outline</v-icon
                            >
                            {{ vPropertyContact.primary_phone }}
                          </div>
                          <div
                            v-if="vPropertyContact.primary_email"
                            class="text-slate-600"
                          >
                            <v-icon small color="slate-500" class="mr-1"
                              >mdi-email-outline</v-icon
                            >
                            {{ vPropertyContact.primary_email }}
                          </div>
                        </div>
                      </template>
                      <template v-else-if="vCustomer?.id">
                        <p class="mb-0 font-size-12 red--text font-weight-500">
                          Please select contact person
                        </p>
                      </template>
                      <template v-else>
                        <p class="mb-0 font-size-12 red--text font-weight-500">
                          Please select customer
                        </p>
                      </template>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="5" class="py-0">
                <table width="100%">
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="reference-no"
                        >Reference #</label
                      >
                    </td>
                    <td>
                      <TextField
                        id="reference-no"
                        dense
                        filled
                        placeholder="Reference #"
                        solo
                        flat
                        counter="50"
                        v-model="contractCreate.reference"
                        color="cyan"
                        :maxlength="50"
                      />
                      <v-text-field
                        v-if="false"
                        id="reference-no"
                        v-model.trim="contractCreate.reference"
                        dense
                        filled
                        :disabled="pageLoading"
                        :loading="pageLoading"
                        color="cyan"
                        placeholder="Reference #"
                        :rules="[
                          validateRules.minLength(
                            contractCreate.reference,
                            'Reference',
                            1
                          ),
                          validateRules.maxLength(
                            contractCreate.reference,
                            'Reference',
                            100
                          ),
                        ]"
                        :maxlength="100"
                        solo
                        flat
                      ></v-text-field>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="sales-representatives"
                        >Sales representatives</label
                      >
                    </td>
                    <td>
                      <v-autocomplete
                        dense
                        color="cyan"
                        filled
                        id="sales-representatives"
                        :items="salesExecutiveList"
                        :disabled="pageLoading"
                        :loading="pageLoading"
                        v-model.trim="contractCreate.sales_executive"
                        placeholder="Sales representatives"
                        solo
                        flat
                        item-color="cyan"
                        item-text="full_name"
                        item-value="id"
                        hide-details
                        v-on:change="updateSales()"
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title
                                v-html="'No PIC Found.'"
                              ></v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="prepared-by"
                        >Prepared by</label
                      >
                    </td>
                    <td>
                      <v-text-field
                        dense
                        filled
                        id="prepared-by"
                        color="cyan"
                        :disabled="pageLoading || isFromQuotation"
                        :loading="pageLoading"
                        :rules="[
                          validateRules.minLength(
                            contractCreate.prepared_by,
                            'Prepared by',
                            1
                          ),
                          validateRules.maxLength(
                            contractCreate.prepared_by,
                            'Prepared by',
                            100
                          ),
                        ]"
                        :maxlength="100"
                        v-model.trim="contractCreate.prepared_by"
                        placeholder="Prepared by"
                        solo
                        flat
                        hide-details
                      ></v-text-field>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="billing-frequency"
                        >Billing Frequency</label
                      >
                    </td>
                    <td>
                      <v-select
                        v-model="contractCreate.billing_frequency"
                        :items="billingFrequencyList"
                        item-text="text"
                        item-value="value"
                        dense
                        color="cyan"
                        item-color="cyan"
                        solo
                        flat
                        filled
                        placeholder="Select billing frequency"
                        :rules="[(v) => !!v || 'Billing frequency is required']"
                      ></v-select>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="billing-option"
                        >Billing Option</label
                      >
                    </td>
                    <td>
                      <v-radio-group
                        v-model="contractCreate.billing_option"
                        row
                      >
                        <v-radio
                          v-for="option in billingOptions"
                          :key="option.value"
                          :label="option.text"
                          :value="option.value"
                          class="mr-4"
                        ></v-radio>
                      </v-radio-group>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="duration-value"
                        >Duration</label
                      >
                    </td>
                    <td>
                      <v-layout>
                        <v-flex md6>
                          <v-text-field
                            dense
                            filled
                            id="duration-value"
                            color="cyan"
                            :disabled="pageLoading"
                            :loading="pageLoading"
                            v-mask="'###'"
                            v-model.trim="contractCreate.duration_value"
                            placeholder="Duration"
                            solo
                            flat
                            hide-details
                            v-on:change="durationOnChnage"
                          ></v-text-field>
                        </v-flex>
                        <v-flex md6>
                          <v-select
                            dense
                            color="cyan"
                            filled
                            id="duration-type"
                            :items="durationTypeList"
                            :disabled="pageLoading"
                            :loading="pageLoading"
                            v-model.trim="contractCreate.duration_type"
                            v-on:change="
                              updateDuration(contractCreate.contract_start)
                            "
                            placeholder="Duration"
                            solo
                            flat
                            item-color="cyan"
                            hide-details
                          >
                            <template v-slot:no-data>
                              <v-list-item>
                                <v-list-item-content>
                                  <v-list-item-title
                                    v-html="'No Duration Type Found.'"
                                  ></v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                            </template>
                          </v-select>
                        </v-flex>
                      </v-layout>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="start-date-picker"
                        >Dates</label
                      >
                    </td>
                    <td>
                      <v-layout>
                        <v-flex md6>
                          <DatePicker
                            solo
                            flat
                            :default-date="contractCreate.contract_start"
                            v-model="contractCreate.contract_start"
                            v-on:change="updateDuration($event)"
                          >
                          </DatePicker>
                        </v-flex>

                        <v-flex md6>
                          <DatePicker
                            solo
                            flat
                            disabled
                            :default-date="contractCreate.contract_end"
                            v-model="contractCreate.contract_end"
                          ></DatePicker>
                        </v-flex>
                      </v-layout>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500 required"
                        for="no-of-services"
                        >No. of Services</label
                      >
                    </td>
                    <td>
                      <v-text-field
                        dense
                        filled
                        id="no-of-services"
                        color="cyan"
                        :disabled="pageLoading"
                        :loading="pageLoading"
                        v-mask="'###'"
                        :rules="[
                          validateRules.required(
                            contractCreate.total_service,
                            'No. of Services'
                          ),
                        ]"
                        v-model.trim="contractCreate.total_service"
                        placeholder="No. of Services"
                        solo
                        flat
                        hide-details
                      ></v-text-field>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="checklists"
                        >Checklist</label
                      >
                    </td>
                    <td>
                      <v-autocomplete
                        dense
                        color="cyan"
                        filled
                        id="checklists"
                        :items="checklistOptions"
                        :disabled="pageLoading"
                        :loading="pageLoading"
                        v-model.trim="contractCreate.checklist"
                        placeholder="Checklist"
                        solo
                        flat
                        item-color="cyan"
                        item-text="title"
                        item-value="id"
                        hide-details
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title
                                v-html="'No Checklist Found.'"
                              ></v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </td>
                  </tr>
                  <tr>
                    <td width="150">
                      <label
                        class="font-size-16 font-weight-500"
                        for="quotation"
                        >Quotation</label
                      >
                    </td>
                    <td>
                      <v-autocomplete
                        dense
                        color="cyan"
                        filled
                        id="quotation"
                        :items="quotationOptions"
                        :disabled="pageLoading"
                        :loading="pageLoading"
                        v-model.trim="contractCreate.quotation"
                        placeholder="Select Quotation"
                        solo
                        flat
                        item-color="cyan"
                        item-text="job_title"
                        item-value="id"
                        hide-details
                        @change="onQuotationChange"
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title
                                v-html="'No Quotation Found.'"
                              ></v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </td>
                  </tr>
                </table>
              </v-col>
              <v-col cols="12" ref="qtLineItem">
                <v-card flat class="custom-grey-border remove-border-radius">
                  <v-card-title
                    class="headline grey lighten-4"
                    @click="show_line_items = !show_line_items"
                  >
                    <h3
                      class="font-weight-700 custom-headline color-custom-blue"
                    >
                      <v-icon>{{
                        !show_line_items ? "mdi-chevron-down" : "mdi-chevron-up"
                      }}</v-icon>
                      Line Items
                    </h3>
                  </v-card-title>
                  <v-expand-transition>
                    <v-card-text
                      v-show="show_line_items"
                      ref="lineItem"
                      class="font-size-16 py-0"
                    >
                      <line-item
                        v-if="related_type > 0"
                        :related-type="related_type"
                        :unit="unit"
                        :related-detail="updatedContractData"
                        :db-line-items="dbLineItems"
                        :db-equipments="dbEquipments"
                        is-contract
                      />
                    </v-card-text>
                  </v-expand-transition>
                </v-card>
              </v-col>
              <v-col cols="12" class="px-0">
                <Description
                  :trip-description="contractCreate.trip_description"
                  :billing-description="contractCreate.billing_description"
                  :page-loading="pageLoading"
                  @update:trip-description="
                    (val) => (contractCreate.trip_description = val)
                  "
                  @update:billing-description="
                    (val) => (contractCreate.billing_description = val)
                  "
                />
              </v-col>
              <v-col cols="12">
                <v-card
                  flat
                  class="custom-grey-border remove-border-radius"
                  :disabled="pageLoading"
                >
                  <v-card-title
                    class="headline grey lighten-4"
                    @click="show_terms = !show_terms"
                  >
                    <h3
                      class="font-weight-700 custom-headline color-custom-blue"
                    >
                      <v-icon>
                        {{
                          !show_terms ? "mdi-chevron-down" : "mdi-chevron-up"
                        }}
                      </v-icon>
                      Terms & Conditions
                    </h3>
                  </v-card-title>

                  <v-expand-transition>
                    <v-card-text class="p-6 font-size-16" v-show="show_terms">
                      <v-autocomplete
                        dense
                        color="cyan"
                        filled
                        label="Terms &amp; Conditions"
                        solo
                        flat
                        class="mb-2"
                        item-color="cyan"
                        item-text="label"
                        item-value="id"
                        hide-details
                        :disabled="formLoading"
                        :loading="formLoading"
                        :items="termsConditionList"
                        v-model="contractCreate.term_condition_id"
                        v-on:change="update_term_condition()"
                      />
                      <editor
                        :disabled="formLoading"
                        v-model="contractCreate.term_conditions"
                      />
                    </v-card-text>
                  </v-expand-transition>
                </v-card>
              </v-col>
              <v-col cols="12" class="px-0">
                <CustomerResponsibilty
                  :added-services="contractCreate.value_added_services"
                  :customer-responsibility="
                    contractCreate.customer_responsibility
                  "
                  :page-loading="pageLoading"
                  @update:added-services="
                    (val) => (contractCreate.value_added_services = val)
                  "
                  @update:customer-responsibility="
                    (val) => (contractCreate.customer_responsibility = val)
                  "
                />
              </v-col>
              <v-col cols="12" ref="qtAttachment">
                <v-container fluid class="pa-0">
                  <CollapsibleDocumnetList
                    :document-type="22"
                    :extensive="false"
                    v-model="contractCreate.documents"
                  >
                  </CollapsibleDocumnetList>
                </v-container>
              </v-col>
              <v-col cols="12" v-if="false">
                <v-card flat class="custom-grey-border remove-border-radius">
                  <v-card-title
                    class="headline grey lighten-4"
                    @click="show_po_attachment = !show_po_attachment"
                  >
                    <h3
                      class="font-weight-700 custom-headline color-custom-blue d-flex align-center"
                    >
                      <v-icon class="mr-2">
                        {{
                          !show_po_attachment
                            ? "mdi-chevron-down"
                            : "mdi-chevron-up"
                        }}
                      </v-icon>
                      ATTACHMENT
                    </h3>
                  </v-card-title>

                  <!-- Toggle content -->
                  <v-expand-transition>
                    <div v-show="show_po_attachment">
                      <v-layout class="mt-4">
                        <v-flex md12>
                          <DocumnetList
                            v-model="contractCreate.documents"
                            :document-type="22"
                            :extensive="false"
                          ></DocumnetList>
                        </v-flex>
                      </v-layout>
                    </div>
                  </v-expand-transition>
                </v-card>
              </v-col>
            </v-row>
          </v-form>

          <customer-select
            :dialog="reCustomerDialog"
            :show-customer-create-dialog="false"
            @close="closeCustomerDialog"
            @create="createCustomerDialog"
          />
          <customer-create :key="`quotation-customer-create-${dialog_key}`" />
          <person-select
            :dialog="contactDialog"
            v-on:close="contactDialog = false"
            :type="contactType"
          />
          <address-select
            :zones="activeZones"
            :dialog="propertyDialog"
            v-on:close="propertyDialog = false"
            :type="propertyType"
            :label="propertyLabel"
          />
        </div>
      </perfect-scrollbar>
    </template>
  </CreateUpdateTemplate>
</template>

<script>
import CreateUpdateTemplate from "@/view/pages/partials/Create-Update-Template.vue";
import { PreventDialogEventBus } from "@/core/lib/prevent.reload.lib";
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ValidationMixin from "@/core/plugins/validation-mixin";
import LineItemV2 from "@/view/pages/partials/Line-Item-V2.vue";
import moment from "moment-timezone";
import ObjectPath from "object-path";
import TinyMCE from "@/view/pages/partials/TinyMCE.vue";
import {
  SET_DIALOG_STATUS,
  SET_CREATE_DIALOG_STATUS,
  CLEAR_CUSTOMER,
} from "@/core/services/store/customer.module";
import {
  SET_CUSTOMER,
  SET_PROPERTY,
  SET_PROPERTY_CONTACT,
  SET_BILLING,
  SET_BILLING_CONTACT,
  RESET_CREATE_STATE,
} from "@/core/services/store/visit.module";
import { QUERY, POST, PUT } from "@/core/services/store/request.module";
import CustomerSelect from "@/view/components/CustomerSelect.vue";
import CustomerCreate from "@/view/components/CustomerCreateDialog.vue";
import PersonSelect from "@/view/components/PersonSelect.vue";
import AddressSelect from "@/view/components/AddressSelect.vue";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import KTCookie from "@/assets/js/components/cookie";
import TextAreaField from "@/view/components/app-component/TextAreaField.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import DocumnetList from "@/view/pages/DocumnetList";
import Description from "@/view/pages/partials/Description.vue";
import CustomerResponsibilty from "@/view/pages/partials/Services-CustomerResponsibilty.vue";
import CollapsibleDocumnetList from "@/view/pages/CollapsibleDocumnetList";
import { cloneDeep, isEmpty, toSafeInteger } from "lodash";
import DatePicker from "@/view/pages/partials/Datepicker.vue";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  name: "create-quotation",
  mixins: [ValidationMixin, CommonMixin],
  data() {
    return {
      pageLoading: false,
      dialog_key: Number(new Date()),
      formLoading: false,
      formValid: true,
      contractCreated: false,
      show_line_items: false,
      related_type: 0,
      tag_type: null,
      contractId: 0,
      billingOptions: [
        { text: "One-time billing", value: "One-time billing" },
        { text: "Bill per visit", value: "Bill per visit" },
      ],
      durationTypeList: [
        { text: "Year", value: "year" },
        { text: "Month", value: "month" },
      ],
      billingFrequencyList: [
        { text: "Monthly", value: "monthly" },
        { text: "Bi Monthly", value: "bi-monthly" },
        { text: "Quarterly", value: "quarterly" },
        { text: "Every 4 Months", value: "every-4-months" },
        { text: "Semi Annually", value: "Semi-annually" },
        { text: "Annually", value: "annually" },
        { text: "Quarterly", value: "quarterly" },
      ],
      show_po_attachment: true,
      contractCreate: {
        job_title: null,
        billing_frequency: "monthly",
        billing_option: "one-time billing",
        trip_description: null,
        billing_description: null,
        documents: [],
        value_added_services: `<ul>
                                        <li>Provide Fault Docket &amp; Air-Conditioner's Service History Log.</li>
                                        <li>Equipment tagging for air-con equipment.</li>
                                        <li>Provide 6 days × 8 hours × 4 hours Response Time for Air-Conditioner's Breakdown.</li>
                                        </ul>`,
        customer_responsibility: `<p>
                                              The maintenance is valid only if there are no changes made to the existing air-conditioning network. 
                                              If any changes are made to the existing network by the customer, the site technician will not be able to provide support after those changes.
                                              The site technician provided by our company has the right to refuse support if such changes are detected. 
                                              Additional fees or charges may apply for maintaining the unit if modifications affect the maintenance process.
                                          </p>`,
        term_condition_id: 0,
        term_conditions: null,
        description: null,
        property_person_notify: 1,
        billing_person_notify: 1,
        rating: 1,
        reference: null,
        duration_value: 1,
        duration_type: "year",
        contract_start: moment().format("YYYY-MM-DD"),
        contract_end: moment().add(1, "year").format("YYYY-MM-DD"),
        sales_executive: null,
        prepared_by: null,
        is_contract: true,
        duration: null,
        total_service: null,
        remark: null,
        discount_value: null,
        discount_value_type: null,
        tax_applied: null,
        adjustment: null,
        sales: null,
        checklist: null,
        quotation: 0,
        terms: {
          quote: null,
          validity: null,
        },
      },
      unit: [],
      dbLineItems: [],
      dbEquipments: [],
      todayDate: null,
      updatedContractData: {},
      propertyType: null,
      propertyLabel: null,
      propertyDialog: false,
      contactType: null,
      contactDialog: false,
      activeZones: [],
      reviseContract: 0,
      salesExecutiveList: [],
      checklistOptions: [],
      quotationOptions: [],
      quotationRequestId: 0,
      termsConditionList: [],
      isFromQuotation: false,
      isManualQuotationChange: false,
      show_terms: true,
    };
  },
  components: {
    "customer-select": CustomerSelect,
    "customer-create": CustomerCreate,
    "person-select": PersonSelect,
    "address-select": AddressSelect,
    "line-item": LineItemV2,
    editor: TinyMCE,
    TextAreaField,
    CreateUpdateTemplate,
    Description,
    DatePicker,
    CustomerResponsibilty,
    DocumnetList,
    CollapsibleDocumnetList,
  },
  methods: {
    onQuotationChange() {
      this.isManualQuotationChange = true;
    },
    update_term_condition() {
      const output = this.lodash.find(this.termsConditionList, {
        id: this.contractCreate.term_condition_id,
      });
      if (output) {
        this.contractCreate.term_conditions = output.description;
      }
    },
    normalizeDocuments(items) {
      if (!Array.isArray(items)) {
        return [];
      }

      return items
        .map((item) => {
          if (item == null) return null;
          if (typeof item === "number") return item;
          if (typeof item === "string" && /^[0-9]+$/.test(item)) {
            return Number(item);
          }
          if (item?.id) return Number(item.id);
          return null;
        })
        .filter((item) => item !== null);
    },
    updateSales() {
      const sales_executive = find(this.salesExecutiveList, {
        id: this.contractCreate.sales_executive,
      });
      if (sales_executive) {
        this.contractCreate.sales = sales_executive.full_name;
      }
    },
    durationOnChnage() {
      if (this.contractCreate.duration_value < 1) {
        this.contractCreate.duration_value = 1;
      }
      this.updateDuration(this.contractCreate.contract_start);
    },

    updateDuration(contract_start) {
      this.contractCreate.contract_start = contract_start;

      this.$nextTick(() => {
        const { duration_value, duration_type } = this.contractCreate;

        let output = moment(contract_start).add(duration_value, duration_type);
        this.contractCreate.contract_end = moment(output)
          .subtract(1, "day")
          .format("YYYY-MM-DD");
      });
    },
    createCustomerDialog() {
      this.$store.dispatch(CLEAR_CUSTOMER);
      this.$store.commit(SET_CUSTOMER, {});
      this.$store.commit(SET_PROPERTY, {});
      this.$store.commit(SET_PROPERTY_CONTACT, {});
      this.$store.commit(SET_BILLING, {});
      this.$store.commit(SET_BILLING_CONTACT, {});
      this.dialog_key = Number(new Date());
      this.$nextTick(() => {
        this.$store.commit(SET_CREATE_DIALOG_STATUS, true);
      });
    },

    closeCustomerDialog() {
      this.$store.commit(SET_DIALOG_STATUS, false);
    },
    openDialog(type) {
      this.$store.commit(SET_DIALOG_STATUS, false);
      this.propertyType = null;
      this.propertyLabel = null;
      this.propertyDialog = false;
      this.contactType = null;
      this.contactDialog = false;

      if (type == "customer") {
        this.$store.commit(SET_DIALOG_STATUS, true);
      } else {
        if (!this.vCustomerId) {
          ErrorEventBus.$emit("update:error", "Please Select Customer.");
          return false;
        }
        if (type == "billing") {
          this.propertyType = "billing";
          this.propertyLabel = "Billing Location";
          this.propertyDialog = true;
        }
        if (type == "billing-contact") {
          this.contactType = "billing";
          this.contactDialog = true;
        }
        if (type == "property") {
          this.propertyType = "property";
          this.propertyLabel = "Service Location";
          this.propertyDialog = true;
        }
        if (type == "property-contact") {
          this.contactType = "property";
          this.contactDialog = true;
        }
      }
    },
    async updateOrCreate(type, statusapprove) {
      const _this = this;
      if (_this.formLoading || _this.contractCreated) {
        return false;
      }
      //console.log(statusapprove);
      const validateStatus = _this.$refs.contractForm.validate();

      const formErrors = _this.validateForm(_this.$refs.contractForm);

      if (formErrors.length) {
        for (let i = 0; i < formErrors.length; i++) {
          ErrorEventBus.$emit("update:error", InitializeError(formErrors[i]));
        }
        return false;
      }
      if (_this.contractCreate.total_service == 0) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Select Services then try again.")
        );
        return false;
      }
      if (!this.vCustomerId) {
        ErrorEventBus.$emit("update:error", "Select Customer");
        return false;
      }

      if (!this.vPropertyId) {
        ErrorEventBus.$emit("update:error", "Select Service Location");
        return false;
      }

      if (!this.vPropertyContactId) {
        ErrorEventBus.$emit("update:error", "Select Service Contact Person");
        return false;
      }

      if (!this.vBillingId) {
        ErrorEventBus.$emit("update:error", "Select Billing Location");
        return false;
      }

      if (!this.vBillingContactId) {
        ErrorEventBus.$emit("update:error", "Select Billing Contact Person");
        return false;
      }

      const allHeaders = this.vSelectedLineItem.every(
        (obj) => obj.type === "header"
      );
      if (
        !this.vSelectedLineItem ||
        !this.vSelectedLineItem.length ||
        allHeaders
      ) {
        ErrorEventBus.$emit("update:error", "Select Line Item");
        return false;
      }

      const AllItems = this.vSelectedLineItem;
      if (AllItems?.length > 0) {
        for (var k = 0; k < AllItems.length; k++) {
          if (!AllItems[k].product) {
            ErrorEventBus.$emit(
              "update:error",
              AllItems[k].type + " title is required."
            );
            return false;
          }
        }
      }

      if (!validateStatus) {
        return false;
      }

      let REQUEST_TYPE = POST;
      let REQUEST_URL = "contract";

      if (_this.contractId && _this.contractId > 0) {
        REQUEST_TYPE = PUT;
        REQUEST_URL = "contract/" + _this.contractId;
      }

      if (_this.reviseContract && _this.reviseContract > 0) {
        type = "revise";
        REQUEST_TYPE = PUT;
        REQUEST_URL = "contract/" + _this.reviseContract;
      }

      const formData = new Object({
        action: typeof type == "string" ? type : undefined,
        statusapprove: statusapprove ? statusapprove : null,
        ticket: _this.ticketId,
        visit: _this.visitId,
        invoice: _this.invoiceId,
        quotation: toSafeInteger(_this.contractCreate.quotation) || 0,

        customer: this.vCustomerId,
        property: this.vPropertyId,
        billing: this.vBillingId,
        property_person: this.vPropertyContactId,
        billing_person: this.vBillingContactId,
        job_title: _this.contractCreate.job_title,
        description: _this.contractCreate.description,
        trip_description: _this.contractCreate.trip_description,
        value_added_services: _this.contractCreate.value_added_services,
        customer_responsibility: _this.contractCreate.customer_responsibility,
        billing_description: _this.contractCreate.billing_description,
        billing_frequency: _this.contractCreate.billing_frequency,
        billing_option: _this.contractCreate.billing_option,
        property_person_notify: toSafeInteger(
          _this.contractCreate.property_person_notify
        ),
        billing_person_notify: toSafeInteger(
          _this.contractCreate.billing_person_notify
        ),
        barcode: _this.contractCreate.barcode,
        reference: _this.contractCreate.reference,
        duration_value: _this.contractCreate.duration_value,
        duration_type: _this.contractCreate.duration_type,
        contract_start: _this.contractCreate.contract_start,
        contract_end: _this.contractCreate.contract_end,
        rating: _this.contractCreate.rating,
        sales_executive: _this.contractCreate.sales_executive,
        prepared_by: _this.contractCreate.prepared_by,
        is_contract: toSafeInteger(_this.contractCreate.is_contract),
        duration: _this.contractCreate.duration || [],
        total_service: toSafeInteger(_this.contractCreate.total_service),
        remark: _this.contractCreate.remark,
        sales: _this.contractCreate.sales,
        quote_terms: _this.terms,
        term_conditions: _this.contractCreate.term_conditions,
        additional_remarks: _this.additional_remarks,
        checklist: _this.contractCreate.checklist,
        "line-item": this.vSelectedLineItem,
        calculation: this.vCalculations,
        documents: _this.normalizeDocuments(_this.contractCreate.documents),
      });

      if (!_this.contractCreated) {
        _this.formLoading = true;
        try {
          const contract = await _this.$store.dispatch(REQUEST_TYPE, {
            url: REQUEST_URL,
            data: formData,
          });

          if (!isEmpty(contract)) {
            _this.contractId = toSafeInteger(contract.data.id);
            _this.contractCreated = true;
            _this.forcePush = true;
          }
        } catch (error) {
          _this.logError(error);
          _this.formLoading = false;
          ErrorEventBus.$emit(
            "update:error",
            InitializeError("Contract is not created. Please try again.")
          );
          return false;
        }
      }
      _this.$nextTick(() => {
        _this.$router.push({
          name: "admin.contract.detail",
          params: {
            id: _this.contractId,
          },
        });
      });
    },
    getOptions() {
      this.pageLoading = true;
      return this.$store
        .dispatch(QUERY, {
          url: "contract/options",
          data: {
            duplicate: this.duplicateContract,
            revise: this.reviseContract,
            contract: this.contractId,
            invoice: this.invoiceId,
            quotation: this.quotationId,
            ticket: this.ticketId,
            visit: this.visitId,
            customer: ObjectPath.get(this.$route, "query.customer") || 0,
            is_dup_changed: this.isDupChanged,
            billing: ObjectPath.get(this.$route, "query.billing") || 0,
            property: ObjectPath.get(this.$route, "query.property") || 0,
            property_person:
              ObjectPath.get(this.$route, "query.property_person") || 0,
            billing_person:
              ObjectPath.get(this.$route, "query.billing_person") || 0,
          },
        })
        .then(({ data }) => {
          if (data.user && data.user.full_name) {
            this.contractCreate.prepared_by = data.user.full_name;
          }

          if (data.barcode) {
            this.contractCreate.barcode = data.barcode;
          }

          if (!isEmpty(data.sales_users)) {
            this.salesExecutiveList = data.sales_users;
          }
          if (!isEmpty(data.checklist)) {
            this.checklistOptions = data.checklist;
          }

          if (!isEmpty(data.options)) {
            this.barcodeSetting = data.options;
          }

          this.termsConditionList = ObjectPath.get(
            data,
            "term_condition_list",
            ObjectPath.get(
              data,
              "term_conditions_list",
              ObjectPath.get(data, "options.term_condition_list", [])
            )
          );

          let defaultTermConditionObject = this.lodash.find(
            this.termsConditionList,
            {
              primary: 1,
            }
          );
          if (
            defaultTermConditionObject &&
            !this.contractCreate.term_condition_id
          ) {
            this.contractCreate.term_condition_id =
              defaultTermConditionObject.id;
            this.contractCreate.term_conditions =
              defaultTermConditionObject.description;
          }

          if (!isEmpty(data.contract)) {
            this.setUpdateData(data.contract);
          }
          if (!isEmpty(data.revise)) {
            this.setReviseData(data.revise);
          }

          if (!isEmpty(data.invoice)) {
            this.setInvoiceData(data.invoice);
          }

          if (!isEmpty(data.quotation)) {
            this.setQuotationData(data.quotation);
          }

          if (!isEmpty(data.ticket)) {
            this.setTicketData(data.ticket);
          }

          if (!isEmpty(data.duplicate)) {
            this.setDuplicateData(data.duplicate);
          }
          this.$store.commit(SET_CUSTOMER, data?.customer);
          this.$store.commit(SET_PROPERTY, data?.property);
          this.$store.commit(SET_PROPERTY_CONTACT, data?.property_person);
          this.$store.commit(SET_BILLING, data?.billing);
          this.$store.commit(SET_BILLING_CONTACT, data?.billing_person);
          this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Contract", route: "contract" },
            {
              title:
                this.$route.name === "admin.contract.update" ||
                this.contractId > 0 ||
                !isEmpty(data.contract)
                  ? "Update"
                  : "Create",
            },
            { title: this.contractCreate.barcode },
          ]);
        })
        .catch((error) => {
          console.log({ error });
          this.logError(error);
          this.goBack();
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    setUpdateData(param) {
      const _this = this;

      _this.contractCreate.id = param.id;
      _this.contractCreate.job_title = param.job_title;
      _this.contractCreate.trip_description = param.trip_description;
      _this.contractCreate.value_added_services = param.value_added_services;
      _this.contractCreate.customer_responsibility =
        param.customer_responsibility;
      _this.contractCreate.term_condition_id = param.term_condition_id || 0;
      _this.contractCreate.term_conditions = param.term_conditions;
      _this.contractCreate.billing_description = param.billing_description;
      _this.contractCreate.billing_frequency = param.billing_frequency;
      _this.contractCreate.billing_option = param.billing_options;
      _this.contractCreate.description = param.description;
      _this.contractCreate.property_person_notify =
        param.property_person_notify;
      _this.contractCreate.billing_person_notify = param.billing_person_notify;
      _this.contractCreate.rating = param.rating;
      _this.contractCreate.reference = param.reference;
      _this.contractCreate.prepared_by = param.prepared_by;
      _this.contractCreate.is_contract = true;
      _this.contractCreate.duration_value = param.duration_value;
      _this.contractCreate.duration_type = param.duration_type;
      _this.contractCreate.contract_start = param.contract_start;
      _this.contractCreate.contract_end = param.contract_end;
      _this.contractCreate.duration = param.duration;
      _this.contractCreate.total_service = param.total_service
        ? param.total_service
        : null;
      _this.contractCreate.remark = param.remark;
      _this.contractCreate.discount_value = param.discount_value;
      _this.contractCreate.discount_value_type = param.discount_value_type;
      _this.contractCreate.discount_type = param.discount_type;
      _this.contractCreate.tax_applied = param.tax_applied;
      _this.contractCreate.adjustment = param.adjustment;
      _this.contractCreate.checklist = param.checklist?.id;
      _this.contractCreate.quotation = String(param.quotation?.id || 0);
      _this.contractCreate.sales_executive = param.sales_representatives?.id;
      _this.isFromQuotation = !!param.quotation;
      _this.updatedContractData = cloneDeep(param);
    },
    setReviseData(param) {
      const _this = this;

      _this.contractCreate.job_title = param.job_title;
      _this.contractCreate.description = param.description;
      _this.contractCreate.trip_description = param.trip_description;
      _this.contractCreate.value_added_services = param.value_added_services;
      _this.contractCreate.customer_responsibility =
        param.customer_responsibility;
      _this.contractCreate.billing_description = param.billing_description;
      _this.contractCreate.term_condition_id = param.term_condition_id || 0;
      _this.contractCreate.term_conditions = param.term_conditions;
      _this.contractCreate.billing_frequency = param.billing_frequency;
      _this.contractCreate.billing_option = param.billing_options;
      _this.contractCreate.property_person_notify =
        param.property_person_notify;
      _this.contractCreate.billing_person_notify = param.billing_person_notify;
      _this.contractCreate.rating = param.rating;
      _this.contractCreate.reference = param.reference;
      _this.contractCreate.sales_executive = param.sales_representatives?.id;
      _this.contractCreate.prepared_by = param.prepared_by;
      _this.contractCreate.is_contract = true;
      _this.contractCreate.duration_value = toSafeInteger(param.duration_value);
      _this.contractCreate.duration_type = param.duration_type;
      _this.contractCreate.contract_start = param.contract_start;
      _this.contractCreate.contract_end = param.contract_end;
      _this.updateDuration(this.contractCreate.contract_start);
      _this.contractCreate.duration = param.duration;
      _this.contractCreate.total_service = param.total_service;
      _this.contractCreate.remark = param.remark;
      _this.contractCreate.discount_value = param.discount_value;
      _this.contractCreate.discount_value_type = param.discount_value_type;
      _this.contractCreate.tax_applied = param.tax_applied;
      _this.contractCreate.adjustment = param.adjustment;
      _this.contractCreate.sales = param.sales;
      // _this.contractCreate.checklist = param.checklist || [];
      // _this.contractCreate.checklist = param.checklist || [];
      _this.contractCreate.checklist = param.checklist?.id || null;
      _this.updatedContractData = cloneDeep(param);
    },
    setDuplicateData(param) {
      const _this = this;

      _this.contractCreate.job_title = param.job_title;
      _this.contractCreate.description = param.description;
      _this.contractCreate.trip_description = param.trip_description;
      _this.contractCreate.value_added_services = param.value_added_services;
      _this.contractCreate.customer_responsibility =
        param.customer_responsibility;
      _this.contractCreate.billing_description = param.billing_description;
      _this.contractCreate.term_condition_id = param.term_condition_id || 0;
      _this.contractCreate.term_conditions = param.term_conditions;
      _this.contractCreate.billing_frequency = param.billing_frequency;
      _this.contractCreate.billing_option = param.billing_options;
      _this.contractCreate.property_person_notify =
        param.property_person_notify;
      _this.contractCreate.billing_person_notify = param.billing_person_notify;
      _this.contractCreate.rating = param.rating;
      _this.contractCreate.reference = `Contract # ${param.barcode}`;
      _this.contractCreate.sales_executive = param.sales_executive;
      _this.contractCreate.prepared_by = param.prepared_by;
      _this.contractCreate.is_contract = true; //param.is_contract;
      _this.contractCreate.duration_value = param.duration_value;
      _this.contractCreate.duration_type = param.duration_type;
      _this.contractCreate.contract_start = param.contract_start;
      _this.contractCreate.contract_end = param.contract_end;
      _this.contractCreate.duration = param.duration;
      _this.contractCreate.total_service = param.total_service;
      _this.contractCreate.remark = param.remark;
      _this.contractCreate.discount_value = param.discount_value;
      _this.contractCreate.discount_value_type = param.discount_value_type;
      _this.contractCreate.tax_applied = param.tax_applied;
      _this.contractCreate.adjustment = param.adjustment;
      _this.contractCreate.sales = param.sales;

      const sales_executive = find(_this.salesExecutiveList, {
        full_name: param.sales,
      });
      if (sales_executive) {
        _this.contractCreate.sales = sales_executive.full_name;
        _this.contractCreate.sales_executive = sales_executive.id;
      }
      _this.updatedContractData = cloneDeep(param);
    },
    setQuotationData(param) {
      const _this = this;

      _this.contractCreate.id = param.id;
      _this.contractCreate.job_title = param.job_title;
      _this.contractCreate.prepared_by = param.prepared_by;
      _this.contractCreate.sales_executive = param.sales_id;
      _this.contractCreate.description = param.description;
      _this.contractCreate.property_person_notify =
        param.property_person_notify;
      _this.contractCreate.billing_person_notify = param.billing_person_notify;
      _this.contractCreate.term_condition_id = param.term_condition_id || 0;
      _this.contractCreate.term_conditions = param.term_conditions;
      _this.contractCreate.reference = `Quotation # ${param.barcode}`;
      _this.contractCreate.discount_value = param.discount_value;
      _this.contractCreate.discount_value_type = param.discount_value_type;
      _this.contractCreate.tax_applied = param.tax_applied;
      _this.contractCreate.adjustment = param.adjustment;
      _this.contractCreate.quotation = String(param.id || 0);
      _this.updatedContractData = cloneDeep(param);
    },
    getCustomer(customerId, contactPersonId, propertyId) {
      this.$store
        .dispatch(QUERY, {
          url: `customer-with-relation/${customerId}`,
          data: {
            contact_person: contactPersonId,
            property: propertyId,
          },
        })
        .then((response) => {
          this.$store.commit(
            SET_CUSTOMER,
            ObjectPath.get(response, "data.customer", {})
          );
          this.$store.commit(
            SET_PROPERTY,
            ObjectPath.get(response, "data.property", {})
          );
          this.$store.commit(
            SET_PROPERTY_CONTACT,
            ObjectPath.get(response, "data.property_contact_person", {})
          );
          this.$store.commit(
            SET_BILLING,
            ObjectPath.get(response, "data.billing", {})
          );
          this.$store.commit(
            SET_BILLING_CONTACT,
            ObjectPath.get(response, "data.billing_contact_person", {})
          );
        })
        .catch(() => {
          this.goBack();
        })
        .finally(() => {
          //
        });
    },
    limitDecimal(event) {
      const value = event.target.value;
      const key = event.key;

      if (
        ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"].includes(key)
      )
        return;

      if (!/[0-9.]/.test(key)) {
        event.preventDefault();
        return;
      }

      if (key === "." && value.includes(".")) {
        event.preventDefault();
        return;
      }

      const decimalIndex = value.indexOf(".");
      if (decimalIndex !== -1) {
        const decimals = value.substring(decimalIndex + 1);
        if (
          decimals.length >= 2 &&
          event.target.selectionStart > decimalIndex
        ) {
          event.preventDefault();
        }
      }
    },
    async fetchQuotations(customerId) {
      const requestId = ++this.quotationRequestId;
      try {
        const response = await this.$store.dispatch(QUERY, {
          url: "contract/quotation",
          data: {
            customer: customerId,
          },
        });

        if (
          requestId !== this.quotationRequestId ||
          Number(customerId) !== Number(this.vCustomerId)
        ) {
          return;
        }

        this.quotationOptions = response?.data || [];
      } catch (error) {
        if (requestId !== this.quotationRequestId) {
          return;
        }

        console.log(error);
        this.quotationOptions = [];
      }
    },
  },

  watch: {
    vCustomer: {
      handler(newVal) {
        if (newVal?.sales_person) {
          this.contractCreate.sales_executive = newVal.sales_person;
        }
      },
      deep: true,
      immediate: true,
    },
    vCustomerId: {
      handler(newVal, oldVal) {
        if (
          Number(oldVal) > 0 &&
          Number(oldVal) !== Number(newVal || 0)
        ) {
          this.contractCreate.quotation = 0;
          this.isManualQuotationChange = false;
          this.related_type = 4;
          this.updatedContractData = {};
        }

        if (newVal) {
          this.fetchQuotations(newVal);
        } else {
          this.quotationRequestId += 1;
          this.quotationOptions = [];
        }
      },
      immediate: true,
    },

    "contractCreate.quotation": {
      handler(val) {
        //  Only run when user changed it
        if (!this.isManualQuotationChange) return;

        if (val && Number(val) > 0) {
          this.related_type = 1;

          const selectedQuotation = this.quotationOptions.find(
            (q) => Number(q.id) === Number(val)
          );

          if (selectedQuotation) {
            this.updatedContractData = { ...selectedQuotation };
            this.contractCreate.term_condition_id =
              selectedQuotation.term_condition_id ||
              this.contractCreate.term_condition_id;
            this.contractCreate.term_conditions =
              selectedQuotation.term_conditions ||
              this.contractCreate.term_conditions;
          }
        } else {
          this.related_type = 4;
          this.updatedContractData = {};
        }
      },
      immediate: false,
    },
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Contract", route: "contract" },
      {
        title:
          this.$route.name === "admin.contract.update" ? "Update" : "Create",
      },
    ]);

    this.$refs.contractForm.validate();
    const customerId = Number(this.$route?.query?.customer ?? 0);
    const contactPersonId = Number(this.$route?.query?.contact_person ?? 0);
    const propertyId = Number(this.$route?.query?.property ?? 0);
    this.contractId = toSafeInteger(this.$route.params.id);

    await this.getOptions();

    if (customerId) {
      this.getCustomer(customerId, contactPersonId, propertyId);
    }

    // If quotation id is present in query, auto-select it
    if (this.quotationId && Number(this.quotationId) > 0) {
      this.contractCreate.quotation = String(this.quotationId);

      // Ensure quotation options are loaded (fetch by customer if necessary)
      if (!this.quotationOptions || !this.quotationOptions.length) {
        const cid = this.vCustomerId || customerId || 0;
        try {
          await this.fetchQuotations(cid);
        } catch (e) {
          // ignore
        }
      }

      const selectedQuotation = this.quotationOptions.find(
        (q) => Number(q.id) === Number(this.quotationId)
      );

      if (selectedQuotation) {
        this.updatedContractData = { ...selectedQuotation };
        this.contractCreate.term_condition_id =
          selectedQuotation.term_condition_id ||
          this.contractCreate.term_condition_id;
        this.contractCreate.term_conditions =
          selectedQuotation.term_conditions ||
          this.contractCreate.term_conditions;
        this.related_type = 1;
      }
      this.tag_type = "visit";
    } else {
      this.related_type = 4;
      this.tag_type = "contract";
    }

  },
  beforeMount() {
    this.contractId = toSafeInteger(this.$route.params.id);
    this.duplicateContract = toSafeInteger(this.$route.query.duplicate);
    this.reviseContract = toSafeInteger(this.$route.query.revise);
    this.ticketId = toSafeInteger(this.$route.query.ticket);
    this.visitId = toSafeInteger(this.$route.query.visit);
    this.invoiceId = toSafeInteger(this.$route.query.invoice);
    this.quotationId = toSafeInteger(this.$route.query.quotation);
    this.isDupChanged = toSafeInteger(this.$route.query.isDupChanged);
  },
  destroyed() {
    this.$store.dispatch(CLEAR_CUSTOMER);
    this.$store.dispatch(RESET_CREATE_STATE);
  },
  beforeDestroy() {
    KTCookie.setCookie("kt_aside_toggle_state", "off");
    localStorage.setItem("kt_aside_toggle_state", "off");
  },
  created() {
    this.todayDate = moment().format("YYYY-MM-DD");
  },

  beforeCreate() {
    KTCookie.setCookie("kt_aside_toggle_state", "on");
    localStorage.setItem("kt_aside_toggle_state", "on");
    this.$store.dispatch(CLEAR_CUSTOMER);
    this.$store.dispatch(RESET_CREATE_STATE);
    /*  this.$store.dispatch(CLEAR_LINE_ITEM); */
  },
  computed: {
    ...mapGetters([
      "vCustomer",
      "vProperty",
      "vPropertyContact",
      "vBilling",
      "vBillingContact",
      "vCustomerId",
      "vPropertyId",
      "vPropertyContactId",
      "vBillingId",
      "vBillingContactId",
      "reCustomerDialog",
      "vSelectedLineItem",
      "vCalculations",
      "recurringInvoiceScheduleTypeList",
      "invoiceRecurringSchedule",
      "invoiceScheduleOutput",
      "customError",
      "quoteInvoiceParameters",
      "invoiceRawScheduleOutput",
      "dbInvoiceUpdateSchedule",
      "customErrorOcc",
    ]),
    ...mapGetters(["lProcessing"]),
  },
  beforeRouteLeave(to, from, next) {
    const _this = this;
    if (_this.contractCreated) {
      next();
    } else {
      _this.routePreventDialog = true;
      PreventDialogEventBus.$on("stay-on-page", function () {
        _this.routePreventDialog = false;
        next(false);
      });
      PreventDialogEventBus.$on("leave-page", function () {
        _this.routePreventDialog = false;
        next();
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.quotation-create-page {
  border-top: solid 8px #f7941e;

  label {
    padding-left: 5px;
    font-weight: 600;
  }

  p {
    padding-left: 5px;
  }
}

.form-action {
  display: flex;
  justify-content: space-between;

  .form-title {
    font-size: 24px;
    font-family: "Poppins", Helvetica, Arial, sans-serif;
    font-weight: 700;
    color: #24326d;
    letter-spacing: 1.5px;
    line-height: 1.11em;

    .form-title-link {
      color: #4d6974;
      border-bottom: dashed 2px #4d6974;
    }
  }
}
</style>
